import React, { useEffect, useRef, useState } from 'react';
import { Sparkles, Check, X, Camera, AlertTriangle } from 'lucide-react';
import { useAnalyzeVision } from '../hooks/useAiVision';
import type { VisionSuggestion } from '../hooks/useAiVision';
import { isOpenRouterVisionEnabled } from '../utils/featureEnv';
import { compressDataUrl } from '../utils/compressImage';

interface ImageRecognitionModalProps {
  isOpen: boolean;
  onClose: () => void;
  imageData: string;
  onConfirm: (suggestion: VisionSuggestion) => void;
}

const ImageRecognitionModal: React.FC<ImageRecognitionModalProps> = ({
  isOpen,
  onClose,
  imageData,
  onConfirm,
}) => {
  const visionEnabled = isOpenRouterVisionEnabled();
  const { mutateAsync: analyzeVision, isPending: isAnalyzing } = useAnalyzeVision();
  const analyzeVisionRef = useRef(analyzeVision);
  analyzeVisionRef.current = analyzeVision;
  const [suggestions, setSuggestions] = useState<VisionSuggestion[]>([]);
  const [selectedSuggestion, setSelectedSuggestion] = useState<VisionSuggestion | null>(null);
  const [analysisWarning, setAnalysisWarning] = useState<string | null>(null);
  const [displayImage, setDisplayImage] = useState(imageData);

  useEffect(() => {
    setDisplayImage(imageData);
  }, [imageData]);

  useEffect(() => {
    if (!isOpen || !imageData) return;

    if (!visionEnabled) {
      setSuggestions([]);
      setSelectedSuggestion(null);
      setAnalysisWarning(null);
      return;
    }

    setAnalysisWarning(null);
    setSuggestions([]);
    setSelectedSuggestion(null);

    let cancelled = false;
    void (async () => {
      try {
        const compressed = await compressDataUrl(imageData);
        if (cancelled) return;
        setDisplayImage(compressed);
        const data = await analyzeVisionRef.current(compressed);
        if (cancelled) return;
        setSuggestions(data.suggestions);
        setSelectedSuggestion(data.suggestions[0] ?? null);
        if (data.source === 'fallback') {
          const hint =
            data.warning?.includes('image') || data.warning?.includes('Provider')
              ? 'Photo non reconnue par le modèle (format ou taille). Réessayez ou saisissez manuellement.'
              : data.warning || 'Analyse IA indisponible — saisie manuelle possible';
          setAnalysisWarning(hint);
        }
      } catch {
        if (cancelled) return;
        setSuggestions([]);
        setSelectedSuggestion(null);
        setAnalysisWarning('Impossible de joindre le service IA. Complétez la fiche manuellement.');
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [isOpen, imageData, visionEnabled]);

  const handleConfirm = () => {
    if (selectedSuggestion) {
      onConfirm(selectedSuggestion);
      onClose();
    }
  };

  const handleCustomSuggestion = () => {
    onConfirm({
      nom: '',
      categorie: '',
      sousCategorie: '',
      confidence: 0,
      description: 'Suggestion personnalisée',
    });
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 flex justify-center items-center">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4">
        <div className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-bold text-gray-900 flex items-center">
              <Sparkles className="h-6 w-6 mr-2 text-yellow-500" />
              Reconnaissance d&apos;image
              {visionEnabled && (
                <span className="ml-2 text-xs font-normal text-purple-700 bg-purple-50 px-2 py-0.5 rounded">
                  OpenRouter
                </span>
              )}
            </h2>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600" type="button">
              <X className="h-6 w-6" />
            </button>
          </div>

          <div className="mb-6">
            <img
              src={displayImage}
              alt="Image analysée"
              className="w-full h-48 object-cover rounded-lg border border-gray-200"
            />
          </div>

          {analysisWarning && (
            <div className="mb-4 flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
              <AlertTriangle className="h-5 w-5 shrink-0 mt-0.5" />
              <span>{analysisWarning}</span>
            </div>
          )}

          {!visionEnabled ? (
            <div className="space-y-4">
              <p className="text-sm text-gray-600">
                La vision IA est disponible uniquement en qualif. Utilisez la saisie manuelle.
              </p>
              <div
                className="p-4 border border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-gray-400 transition-colors"
                onClick={handleCustomSuggestion}
                onKeyDown={(e) => e.key === 'Enter' && handleCustomSuggestion()}
                role="button"
                tabIndex={0}
              >
                <div className="flex items-center">
                  <Camera className="h-5 w-5 mr-3 text-gray-400" />
                  <div>
                    <h4 className="font-medium text-gray-900">Saisie manuelle</h4>
                    <p className="text-sm text-gray-500">Compléter nom et catégorie vous-même</p>
                  </div>
                </div>
              </div>
            </div>
          ) : visionEnabled && isAnalyzing ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4" />
              <p className="text-gray-600">Analyse de l&apos;image en cours…</p>
              <p className="text-sm text-gray-500 mt-2">OpenRouter — catégorie et nom suggérés</p>
            </div>
          ) : (
            <div className="space-y-4">
              {suggestions.length > 0 && (
                <>
                  <h3 className="text-lg font-medium text-gray-900">Suggestions détectées :</h3>
                  {suggestions.map((suggestion, index) => (
                    <div
                      key={`${suggestion.nom}-${index}`}
                      className={`p-4 border rounded-lg cursor-pointer transition-colors ${
                        selectedSuggestion === suggestion
                          ? 'border-blue-500 bg-blue-50'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                      onClick={() => setSelectedSuggestion(suggestion)}
                      onKeyDown={(e) => e.key === 'Enter' && setSelectedSuggestion(suggestion)}
                      role="button"
                      tabIndex={0}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <h4 className="font-medium text-gray-900">{suggestion.nom}</h4>
                          <p className="text-sm text-gray-600">
                            {suggestion.categorie}
                            {suggestion.sousCategorie ? ` — ${suggestion.sousCategorie}` : ''}
                          </p>
                          {suggestion.description && (
                            <p className="text-xs text-gray-500 mt-1">{suggestion.description}</p>
                          )}
                        </div>
                        <div className="flex items-center space-x-2">
                          <div className="text-sm text-gray-500">
                            {Math.round(suggestion.confidence * 100)}%
                          </div>
                          {selectedSuggestion === suggestion && (
                            <Check className="h-5 w-5 text-blue-600" />
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </>
              )}

              <div
                className="p-4 border border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-gray-400 transition-colors"
                onClick={handleCustomSuggestion}
                onKeyDown={(e) => e.key === 'Enter' && handleCustomSuggestion()}
                role="button"
                tabIndex={0}
              >
                <div className="flex items-center">
                  <Camera className="h-5 w-5 mr-3 text-gray-400" />
                  <div>
                    <h4 className="font-medium text-gray-900">Saisie manuelle</h4>
                    <p className="text-sm text-gray-500">Je ne trouve pas l&apos;objet dans les suggestions</p>
                  </div>
                </div>
              </div>

              <div className="flex space-x-3 pt-4">
                <button
                  type="button"
                  onClick={handleConfirm}
                  disabled={!selectedSuggestion}
                  className="flex-1 flex items-center justify-center px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  <Check className="h-5 w-5 mr-2" />
                  Confirmer et continuer
                </button>
                <button
                  type="button"
                  onClick={onClose}
                  className="px-4 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Annuler
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ImageRecognitionModal;
